// packages/ui/src/forms/fields/FormAutocomplete.tsx
'use client';

import { Box } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import { useEffect, useMemo, useState } from 'react';
import type { TextFieldProps } from '@mui/material';
import { useController, useFormContext, type FieldValues, type Path } from 'react-hook-form';
import type { ReactElement, ReactNode } from 'react';
import InputAdornment from '@mui/material/InputAdornment';

import { useFormT } from '../_i18n/FormI18nContext';
import { sharedFieldRowSx, sharedLeftIconBoxSx } from './_styles';
import { StableAutocomplete, type StableAutocompleteComponent } from '@tourtask/ui/inputs/StableAutocomplete';

export type FormAutocompleteProps<TFieldValues extends FieldValues, TOption> = Props<TFieldValues, TOption>;

/** FormAutocomplete's own type, which `useCountrySelectorInput` takes (MUI's, or the chat widget's outlined one). */
export type FormAutocompleteComponent = <TFieldValues extends FieldValues, TOption>(
  props: FormAutocompleteProps<TFieldValues, TOption>,
) => ReactElement;

type Props<TFieldValues extends FieldValues, TOption> = {
  name: Path<TFieldValues>;
  options: readonly TOption[];

  label: string;
  disabled?: boolean;
  required?: boolean;

  getOptionLabel: (opt: TOption) => string;
  getOptionValue: (opt: TOption) => string;
  renderOption?: (opt: TOption) => ReactNode;
  getSelectedAdornment?: (opt: TOption | null) => ReactNode;

  helperText?: string;
  leftIcon?: ReactNode;
  highlightWhenEmpty?: boolean;
  /** See `getFieldFillSx`. Defaults to `'white'`. */
  background?: 'white' | 'transparent';
  /**
   * For fields that should never legitimately be empty (the user re-selects
   * rather than clears): hides the built-in clear (X) button, and erasing the
   * text only starts a new search, never clears the value.
   */
  disableClearable?: boolean;
  textFieldProps?: Omit<TextFieldProps, 'label' | 'error' | 'helperText'>;
  selectSingleFilteredOptionOnEnter?: boolean;
  /**
   * Override MUI's default client-side filter. Threaded through to the
   * underlying Autocomplete and reused by the `selectSingleFilteredOptionOnEnter`
   * detection so both stay in sync.
   */
  filterOptions?: (options: TOption[], state: FilterOptionsState<TOption>) => TOption[];
};

import { renderFieldError } from '../_i18n/renderFieldError';


export function FormAutocomplete<TFieldValues extends FieldValues, TOption>(props: Props<TFieldValues, TOption>) {
  return useFormAutocomplete(StableAutocomplete, props);
}

/**
 * FormAutocomplete, drawn with `StableAutocompleteComponent` (see `useFormTextField`: the chat widget's pre-chat
 * form draws its country field with an outlined-only one).
 */
export function useFormAutocomplete<TFieldValues extends FieldValues, TOption>(
  StableAutocompleteComponent: StableAutocompleteComponent,
  {
    name,
    options,
    label,
    disabled,
    required,
    getOptionLabel,
    getOptionValue,
    renderOption,
    getSelectedAdornment,
    helperText,
    leftIcon,
    highlightWhenEmpty = false,
    background = 'white',
    disableClearable = false,
    textFieldProps,
    selectSingleFilteredOptionOnEnter = false,
    filterOptions,
  }: Props<TFieldValues, TOption>,
): ReactElement {
  const { control } = useFormContext<TFieldValues>();
  const t = useFormT();

  const { field, fieldState } = useController({ name, control });

  const valueId = String(field.value ?? '');
  const selected = useMemo(() => {
    return options.find((o) => getOptionValue(o) === valueId) ?? null;
  }, [options, getOptionValue, valueId]);

  const errorText = renderFieldError(fieldState.error?.message, t);

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInputValue(selected ? getOptionLabel(selected) : '');
     
  }, [valueId]);

  const safeGetOptionLabel = useMemo(() => {
    return (opt: TOption) => (opt ? getOptionLabel(opt) : '');
  }, [getOptionLabel]);
  const defaultFilterOptions = useMemo(() => createFilterOptions<TOption>(), []);
  const effectiveFilterOptions = filterOptions ?? defaultFilterOptions;
  const filteredOptions = useMemo(
    () =>
      effectiveFilterOptions(options as TOption[], {
        inputValue,
        getOptionLabel: safeGetOptionLabel,
      }),
    [effectiveFilterOptions, inputValue, options, safeGetOptionLabel],
  );
  const selectedAdornment = getSelectedAdornment ? getSelectedAdornment(selected) : null;
  const slotProps = textFieldProps?.slotProps as { input?: object } | undefined;
  const inputProps = slotProps?.input as
    | { startAdornment?: ReactNode } & Record<string, unknown>
    | undefined;

  return (
    <Box sx={sharedFieldRowSx()}>
      {leftIcon ? (
        <Box sx={sharedLeftIconBoxSx()}>
          {leftIcon}
        </Box>
      ) : null}

      {/*
        `disableClearable` is applied via descendant-targeting CSS rather
        than threaded into `StableAutocomplete` — that component is pinned
        to `Autocomplete<T, false, false, false>`, which makes MUI reject
        a runtime-boolean `disableClearable` prop. Hiding the clear
        button at the DOM level achieves the same visual effect without
        touching the shared component's generic signature.

        MUI also adds the `MuiAutocomplete-hasClearIcon` class
        unconditionally (it's tied to the prop, not the icon's presence),
        which reserves ~26px of `padding-right` on the input root for
        the now-invisible button. Pulling that back recovers the
        horizontal space that motivated hiding the icon in the first
        place.
      */}
      <Box
        sx={{
          flex: 1,
          ...(disableClearable && {
            '& .MuiAutocomplete-clearIndicator': { display: 'none' },
            '& .MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot': {
              paddingRight: '39px',
            },
          }),
        }}
      >
        <StableAutocompleteComponent<TOption>
          highlightWhenEmpty={highlightWhenEmpty && selected === null}
          background={background}
          filterOptions={filterOptions}
          options={options as TOption[]}
          value={selected}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(opt) => {
            // MUI reports erased text as a clear (its own `disableClearable`
            // can't be set here, see above). A field that is never empty keeps
            // its value: the empty text searches every option, and leaving the
            // field shows the selected one again.
            if (opt === null && disableClearable) return;
            field.onChange(opt ? getOptionValue(opt) : '');
            setInputValue(opt ? getOptionLabel(opt) : '');
            setOpen(false);
          }}
          inputValue={inputValue}
          onInputChange={(v, reason) => {
            setInputValue(v);
            // Only open on explicit user typing — not on programmatic value resets
            // (e.g. when a controlled value is filled externally, MUI fires reason='reset').
            if (reason === 'input') setOpen(true);
          }}
          label={label}
          disabled={disabled}
          getOptionLabel={safeGetOptionLabel}
          isOptionEqualToValue={(a, b) => getOptionValue(a) === getOptionValue(b)}
          renderOption={renderOption}
          textFieldProps={{
            ...textFieldProps,
            slotProps: {
              ...textFieldProps?.slotProps,
              input: {
                ...inputProps,
                startAdornment: selectedAdornment ? (
                  <InputAdornment position="start">
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: 0.5,
                      }}
                    >
                      {selectedAdornment}
                    </Box>
                  </InputAdornment>
                ) : inputProps?.startAdornment,
              },
            },
            required,
            error: !!fieldState.error,
            helperText: errorText ?? helperText,
            onBlur: field.onBlur,
            onKeyDown: (e) => {
              textFieldProps?.onKeyDown?.(e);
              if (e.defaultPrevented || !selectSingleFilteredOptionOnEnter || e.key !== 'Enter') {
                return;
              }

              if (filteredOptions.length === 1) {
                const match = filteredOptions[0];
                field.onChange(getOptionValue(match));
                setInputValue(getOptionLabel(match));
                setOpen(false);
                e.preventDefault();
              }
            },
          }}
        />
      </Box>
    </Box>
  );
}
